import type { NitroFetchOptions } from "nitropack";
import type { UseApiFetchOptions } from "@edco/apollo/dist/runtime/composables/useApiFetch";
import type { AsyncData } from "#app";
import type { MemberStubDto } from "~/resources/useIsnMemberResource";
import type { ArticleResultDto } from "~/types/results";

export type ArticleResultsDto = {
  results: ArticleResultDto[];
  resultsCount: number;
  itemsPerPage: number;
  currentPage: number;
  pagesCount: number;
};

export type ArticleResponseDto = {
  id: string;
  title: string;
  text: string;
  topics: string[];
  dateAdded: Date;
  author?: MemberStubDto;
  kudosId?: number;
  kudosCount: number;
};

export type SubmitArticleInput = {
  title: string;
  topics: string[];
  link?: string;
  fileId?: number;
};

export type ListRecentArticleInput = {
  includeDrafts?: boolean;
  maxRecords?: number;
  authorMemberNodeId?: number;
  schoolProfileNodeId?: number;
  partnerProfileNodeId?: number;
  authorMemberKey?: string;
  schoolProfileKey?: string;
  partnerProfileKey?: string;
};

export type SearchArticlesParams = {
  page: number;
  itemsPerPage?: number;
  query?: string;
  category?: string;
};

export type ListArticleInput = {
  articleNodeId?: number | string;
  articleTypeId?: string;
  articleTopicTagId?: string;
  authorMemberNodeId?: number;
  schoolProfileNodeId?: number;
  partnerProfileNodeId?: number;
  authorMemberKey?: string;
  schoolProfileKey?: string;
  partnerProfileKey?: string;
  includePublished?: boolean;
  includeUnpublished?: boolean;
  includeDrafts?: boolean;
  includeSnippet?: boolean;
  includeKudos?: boolean;
  includeTopicTags?: boolean;
  includeOtherProperties?: boolean;
  searchString?: string;
};

export function $articleResource () {
  const dependencies = useFetchDependencies();

  function getRecent ( params: ListRecentArticleInput ) {
    const url = "/api/isn/articles/recent";
    const options: NitroFetchOptions<string> = {
      method: "GET",
      params,
    };
    return $apiFetch<ArticleResultDto[]>( url, options, dependencies );
  }

  function getPromoted ( maxRecords = 4 ) {
    const url = "/api/isn/articles/promoted";
    const options: NitroFetchOptions<string> = {
      method: "GET",
      params: { maxRecords },
    };
    return $apiFetch<ArticleResultDto[]>( url, options, dependencies );
  }

  function getRelated ( articleNodeIdOrKey: number | string ) {
    const url = `/api/isn/articles/related/${articleNodeIdOrKey}`;
    const options: NitroFetchOptions<string> = {
      method: "GET",
    };
    return $apiFetch<ArticleResultDto[]>( url, options, dependencies );
  }

  function list ( params: ListArticleInput ) {
    const url = "/api/isn/articles";
    const options: NitroFetchOptions<string> = {
      method: "GET",
      params,
    };
    return $apiFetch<ArticleResultDto[]>( url, options, dependencies );
  }

  function listByContentTag ( articleContentTagId: string, page = 1, itemsPerPage = 12, category?: string ) {
    const url = `/api/isn/articles/by-content-tag/${articleContentTagId}`;
    const options: NitroFetchOptions<string> = {
      method: "GET",
      params: { page, itemsPerPage, category },
    };
    return $apiFetch<ArticleResultsDto>( url, options, dependencies );
  }

  function search ( page = 1, itemsPerPage = 12, query?: string, category?: string ) {
    const url = "/api/isn/articles/search";
    const options: NitroFetchOptions<string> = {
      method: "GET",
      params: { page, itemsPerPage, query, category },
    };
    return $apiFetch<ArticleResultsDto>( url, options, dependencies );
  }

  function listResponses ( articleNodeIdOrKey: number | string, params: { includeTopicTags?: boolean; includeKudosCount?: boolean; includeKudosStatus?: boolean } ) {
    const url = `/api/isn/articles/${articleNodeIdOrKey}/responses`;
    const options: NitroFetchOptions<string> = {
      method: "GET",
      params,
    };
    return $apiFetch<ArticleResponseDto[]>( url, options, dependencies );
  }

  function saveResponse ( articleNodeIdOrKey: number | string, model: SubmitArticleInput ) {
    const url = `/api/isn/articles/${articleNodeIdOrKey}/responses`;
    const options: NitroFetchOptions<string> = {
      method: "POST",
      body: model,
    };
    return $apiFetch<string>( url, options, dependencies );
  }

  function submitArticle ( model: SubmitArticleInput ) {
    const url = "/api/isn/articles";
    const options: NitroFetchOptions<string> = {
      method: "POST",
      body: model,
    };
    return $apiFetch<void>( url, options, dependencies );
  }

  function uploadArticleDraftFile ( file: File ) {
    const url = "/api/isn/articles/drafts";
    const formData = new FormData();
    formData.append( "file", file );
    const options: NitroFetchOptions<string> = {
      method: "POST",
      headers: {
        "Content-Type": "multipart/form-data",
      },
      body: formData,
    };
    return $apiFetch<number>( url, options, dependencies );
  }

  function removeArticleDraftFile ( fileId: number ) {
    const url = `/api/isn/articles/drafts/${fileId}/remove`;
    const options: NitroFetchOptions<string> = {
      method: "POST",
    };
    return $apiFetch<void>( url, options, dependencies );
  }

  return {
    getRecent,
    getPromoted,
    getRelated,
    list,
    listByContentTag,
    search,
    listResponses,
    saveResponse,
    submitArticle,
    uploadArticleDraftFile,
    removeArticleDraftFile,
  };
}

export function useArticleResource () {
  function getRecent ( params: ListRecentArticleInput ): AsyncData<ArticleResultDto[], Error | null> {
    const url = "/api/isn/articles/recent";
    const options: UseApiFetchOptions<ArticleResultDto[]> = {
      method: "GET",
      params,
    };
    return useApiFetch<ArticleResultDto[]>( url, options );
  }

  function getPromoted ( maxRecords = 4 ): AsyncData<ArticleResultDto[], Error | null> {
    const url = "/api/isn/articles/promoted";
    const options: UseApiFetchOptions<ArticleResultDto[]> = {
      method: "GET",
      params: { maxRecords },
    };
    return useApiFetch<ArticleResultDto[]>( url, options );
  }

  function getRelated ( articleNodeIdOrKey: number | string ): AsyncData<ArticleResultDto[], Error | null> {
    const url = `/api/isn/articles/related/${articleNodeIdOrKey}`;
    const options: UseApiFetchOptions<ArticleResultDto[]> = {
      method: "GET",
    };
    return useApiFetch<ArticleResultDto[]>( url, options );
  }

  function list ( params: ListArticleInput ): AsyncData<ArticleResultDto[], Error | null> {
    const url = "/api/isn/articles";
    const options: UseApiFetchOptions<ArticleResultDto[]> = {
      method: "GET",
      params,
    };
    return useApiFetch<ArticleResultDto[]>( url, options );
  }

  function listByContentTag ( articleContentTagId: string, page = 1, itemsPerPage = 12, category?: string ): AsyncData<ArticleResultsDto, Error | null> {
    const url = `/api/isn/articles/by-content-tag/${articleContentTagId}`;
    const options: UseApiFetchOptions<ArticleResultsDto> = {
      method: "GET",
      params: { page, itemsPerPage, category },
    };
    return useApiFetch<ArticleResultsDto>( url, options );
  }

  function search ( params: MaybeRef<SearchArticlesParams> ): AsyncData<ArticleResultsDto, Error | null> {
    const url = "/api/isn/articles/search";

    const moddedParams = computed( () => {
      const unrefParams = unref( params );

      return {
        page: unrefParams.page,
        itemsPerPage: unrefParams.itemsPerPage ?? 12,
        query: unrefParams.query,
        category: unrefParams.category,
      };
    } );

    const options: UseApiFetchOptions<ArticleResultsDto> = {
      method: "GET",
      params: moddedParams,
      watch: [moddedParams],
    };
    return useApiFetch<ArticleResultsDto>( url, options );
  }

  function listResponses ( articleNodeIdOrKey: number | string, params?: { includeTopicTags?: boolean; includeKudosCount?: boolean; includeKudosStatus?: boolean } ): AsyncData<ArticleResponseDto[], Error | null> {
    const url = `/api/isn/articles/${articleNodeIdOrKey}/responses`;
    const options: UseApiFetchOptions<ArticleResponseDto[]> = {
      method: "GET",
      params,
    };
    return useApiFetch<ArticleResponseDto[]>( url, options );
  }

  return {
    getRecent,
    getPromoted,
    getRelated,
    list,
    listByContentTag,
    search,
    listResponses,
  };
}
